import { useMemo } from 'react'

import { isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { type EntityDescriptor, checkEntityAccess } from '@/utils/access-control/perEntity'

export type EntityAccessResult = { allowed: true } | { allowed: false; reason: 'no-entity-permission' }

/**
 * Per-entity RBAC primitive.
 *
 * Reads the current platform user's permissions and runs `checkEntityAccess`
 * against the supplied descriptor. For the `'owners'` kind, the current user's
 * email and role name are auto-injected so callers only have to supply `owners`.
 *
 * Returns `{ allowed: true }` when no `entity` is supplied — keeps the call
 * site simple for components that conditionally check per-entity access.
 */
export function useEntityAccess(entity?: EntityDescriptor): EntityAccessResult {
	const user = useAuthStore(s => s.user)

	return useMemo((): EntityAccessResult => {
		if (!entity) {
			return { allowed: true }
		}

		const platformUser = user && isPlatformUser(user) ? user : undefined
		const permissions = platformUser?.role?.permissions

		// Auto-inject the actor identities for the ownership check, but let the caller
		// override (e.g. impersonation or testing).
		const resolved: EntityDescriptor =
			entity.kind === 'owners'
				? {
						...entity,
						email: entity.email ?? user?.email,
						roleName: entity.roleName ?? platformUser?.role?.name,
					}
				: entity

		return checkEntityAccess(permissions, resolved)
			? { allowed: true }
			: { allowed: false, reason: 'no-entity-permission' }
	}, [entity, user])
}
